'use client';

import { Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import ContactInquiryPanel from '@/app/[locale]/contact/components/ContactInquiryPanel';
import Container from '@/components/ui/Container/Container';
import { HOME_SECTION_PADDING_CLASS } from '@/lib/home-section-spacing';
import type { LeadForm } from '@/types/lead-forms';

interface BlogEnquirySectionProps {
  leadForms?: LeadForm[];
}

const BENEFIT_KEYS = [
  'benefits.productRecommendations',
  'benefits.costEstimates',
  'benefits.nearestDealer',
  'benefits.technicalAnswers',
] as const;

function BenefitCheckIcon() {
  return (
    <span
      className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#E31E24]"
      aria-hidden
    >
      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
    </span>
  );
}

export default function BlogEnquirySection({ leadForms }: BlogEnquirySectionProps) {
  const t = useTranslations('blog.enquiry');

  return (
    <section
      id="enquiry"
      className={`relative scroll-mt-24 bg-black text-white ${HOME_SECTION_PADDING_CLASS}`}
    >
      <Container>
        <div className="grid grid-cols-1 items-start gap-5 sm:gap-6 lg:grid-cols-[1.35fr_1fr]">
          <div className="flex flex-col lg:py-2">
            <h2 className="max-w-xl text-[1.75rem] font-bold leading-[1.12] tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-[1.08]">
              {t('heading')}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55 sm:mt-5 sm:text-base">
              {t('subheading')}
            </p>

            <div className="my-7 border-t border-white/10 sm:my-8" aria-hidden />

            <ul className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 sm:gap-y-6">
              {BENEFIT_KEYS.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <BenefitCheckIcon />
                  <span className="text-sm leading-snug text-white sm:text-[15px]">{t(key)}</span>
                </li>
              ))}
            </ul>
          </div>

          <ContactInquiryPanel leadForms={leadForms} />
        </div>
      </Container>
    </section>
  );
}
